import Decimal from 'decimal.js';
import Numeric from '@/core/types/Numeric';
import ProgressBarSegment from './ProgressBarSegment';
import type { ProgressValue } from './ProgressBarSegment';
import type { VNode } from 'vue';

/** Конструктор семантического типа — для форматирования подписей в том же типе */
export type NumericConstructor = new (value: Decimal.Value) => Numeric;

/**
 * Модель прогресс-бара: сегменты из слота плюс посчитанные sum и total.
 *
 * Правила:
 * - total не задан — 100% это сумма сегментов;
 * - сумма сегментов больше total — она становится новым total;
 *   превышение над заданным total — excess (для подсветки перегруза).
 */
class ProgressBarModel {
  readonly segments: ProgressBarSegment[];

  /** Сумма сегментов */
  readonly sum: Decimal;

  /** 100% бара: total, а если он не задан или меньше суммы — сумма */
  readonly total: Decimal;

  /**
   * Задан ли total. Не задан — null, undefined или пустой Numeric
   * (например, ТС не выбрано и maxWeight нет). Тогда остаток справа
   * не считается и печатается прочерк.
   */
  readonly hasTotal: boolean;

  /** total как его задали (лимит); не задан — ноль */
  readonly declaredTotal: Decimal;

  /** Тип для подписей; null — в баре одни числа */
  readonly numericConstructor: NumericConstructor | null;

  constructor(
    segments: ProgressBarSegment[],
    sum: Decimal,
    total: Decimal,
    hasTotal: boolean,
    declaredTotal: Decimal,
    numericConstructor: NumericConstructor | null,
  ) {
    this.segments = segments;
    this.sum = sum;
    this.total = total;
    this.hasTotal = hasTotal;
    this.declaredTotal = declaredTotal;
    this.numericConstructor = numericConstructor;
  }

  /** Остаток до total */
  get remainder(): Decimal {
    return this.total.minus(this.sum);
  }

  /**
   * Превышение суммы сегментов над заданным total (перегруз).
   * total не задан или не превышен — ноль.
   */
  get excess(): Decimal {
    if (this.hasTotal === true && this.sum.greaterThan(this.declaredTotal) === true) {
      return this.sum.minus(this.declaredTotal);
    } else {
      return new Decimal(0);
    }
  }

  get hasExcess(): boolean {
    return this.excess.isZero() === false;
  }

  /**
   * Модель из vnode слота и пропа total.
   * Вызывается на каждом рендере бара: слот вызывается внутри render,
   * поэтому Vue сам отслеживает зависимости значений сегментов и v-if на тегах.
   */
  static fromSlot(vnodes: VNode[], rawTotal: ProgressValue): ProgressBarModel {
    const segments: ProgressBarSegment[] = [];

    ProgressBarModel.collect(vnodes, segments);

    let sum = new Decimal(0);

    for (const segment of segments) {
      sum = sum.plus(segment.value);
    }

    const hasTotal = ProgressBarModel.isTotalDefined(rawTotal);

    let declaredTotal: Decimal;
    let total: Decimal;

    if (hasTotal === false) {
      declaredTotal = new Decimal(0);
      total = sum;
    } else {
      declaredTotal = ProgressBarSegment.toDecimal(rawTotal);

      if (sum.greaterThan(declaredTotal) === true) {
        total = sum;
      } else {
        total = declaredTotal;
      }
    }

    const rawValues: ProgressValue[] = [rawTotal];

    for (const segment of segments) {
      rawValues.push(...segment.rawValues);
    }

    return new ProgressBarModel(
      segments,
      sum,
      total,
      hasTotal,
      declaredTotal,
      ProgressBarModel.detectNumericConstructor(rawValues),
    );
  }

  /**
   * Обход vnode слота. Рекурсия нужна для v-for и v-if:
   * такие теги приходят обёрнутыми во Fragment.
   */
  private static collect(vnodes: VNode[], segments: ProgressBarSegment[]): void {
    for (const vnode of vnodes) {
      if (!vnode.type) continue;

      const name = (vnode.type as { name?: string }).name;

      if (name === 'VProgressBarSegment') {
        segments.push(ProgressBarSegment.fromVNode(vnode));
      }

      if (Array.isArray(vnode.children)) {
        ProgressBarModel.collect(vnode.children as VNode[], segments);
      }
    }
  }

  /** total задан: не null, не undefined, не пустой Numeric и не NaN */
  private static isTotalDefined(rawTotal: ProgressValue): boolean {
    if (rawTotal === null || rawTotal === undefined) {
      return false;
    } else if (rawTotal instanceof Numeric) {
      return rawTotal.isEmpty() === false;
    } else {
      return Number.isFinite(rawTotal);
    }
  }

  /**
   * Тип для подписей — первый встреченный наследник Numeric (total, затем сегменты).
   * Числа рядом с типом допустимы: progressBar.* с бэка приходят числами.
   * Два разных типа в одном баре — ошибка вызывающей стороны.
   */
  private static detectNumericConstructor(rawValues: ProgressValue[]): NumericConstructor | null {
    let found: NumericConstructor | null = null;

    for (const value of rawValues) {
      if (value instanceof Numeric) {
        const valueConstructor = value.constructor as NumericConstructor;

        if (found === null) {
          found = valueConstructor;
        } else if (found !== valueConstructor) {
          console.warn('VProgressBar: в одном баре разные семантические типы, подписи форматируются по первому', rawValues);
        }
      }
    }

    return found;
  }
}

export default ProgressBarModel;